import { computed } from 'vue';
import { useLanguageStore } from '@/stores/language';
import { useAuthStore } from '@/stores/auth';
import { getRequiredPermissionFor } from '@/router/routePermissions.js';

const PROJECT_ROLES = [
    'Project Manager',
    'Project Owner',
    'Project Sponsor',
    'Project Council',
    'Project Board',
]

const PROGRAM_ROLES = [
    'Program Manager',
    'Program Council',
    'Program Council · Chairperson Role',
    'Program Council · Secretary Role',
    'Program Owner',
    'Program Director',
    'Program Sponsor',
]

const STRATEGY_ROLES = [
    'SMO',
    'SMO Director',
    'Strategy Manager',
    'Strategy Specialist',
]

// The main Strategy workspace is an executive navigation entry. Include the
// canonical planning-role names and their legacy equivalents used by existing users.
// Programs and Portfolios are the PMO's own modules; the admin roles keep them too.
const PMO_ONLY_ROLES = [
    'Super Admin',
    'Admin',
    'PMO',
    'PMO Representative',
]

const STRATEGY_SIDEBAR_ROLES = [
    'Super Admin',
    'SMO',
    'Strategy Department Representative',
    'VP',
    'VP of Strategy & Projects',
]

export function useMenuItems() {
    const { t } = useLanguageStore();
    const authStore = useAuthStore();

    const canManageUsersRoles = computed(() => {
        return authStore.hasAnyRole(['Super Admin', 'Admin'])
    });

    /**
     * Returns true if the user is allowed to see the given menu item.
     * Admin roles bypass permission checks, but explicit role-only items remain exact.
     */
    const canAccess = (item) => {
        if (!item) return false
        if (item.action) return true

        // Role gates describe identity, so an Admin must not satisfy a
        // Super-Admin-only item (or impersonate any other role).
        if (item.requiresRole) {
            const roles = Array.isArray(item.requiresRole) ? item.requiresRole : [item.requiresRole]
            if (!authStore.hasAnyRole(roles)) return false
        }

        // Both administrative roles have full feature permissions.
        if (canManageUsersRoles.value) return true

        // Deny-list: hide item if user has any of the blocked roles
        if (item.blockedRoles) {
            const blocked = Array.isArray(item.blockedRoles) ? item.blockedRoles : [item.blockedRoles]
            if (authStore.hasAnyRole(blocked)) return false
        }

        if (item.requiresPermission) {
            if (item.permissionFallbackRoles) {
                const roles = Array.isArray(item.permissionFallbackRoles) ? item.permissionFallbackRoles : [item.permissionFallbackRoles]
                if (authStore.hasAnyRole(roles)) return true
            }
            return Array.isArray(item.requiresPermission)
                ? authStore.hasAnyPermission(item.requiresPermission)
                : authStore.hasPermission(item.requiresPermission)
        }
        if (!item.to) return true
        const required = getRequiredPermissionFor({ path: item.to })
        if (!required) return true
        return Array.isArray(required)
            ? authStore.hasAnyPermission(required)
            : authStore.hasPermission(required)
    }

    /** Recursively prune menu items the user cannot access. */
    const filterTree = (items) => {
        return items
            .filter(item => !item.hidden)
            .filter(canAccess)
            .map(item => {
                if (Array.isArray(item.items) && item.items.length) {
                    const children = filterTree(item.items)
                    return { ...item, items: children }
                }
                return item
            })
            // Drop empty parent groups
            .filter(item => !Array.isArray(item.items) || item.items.length > 0 || item.to || item.action)
    }

    const menuItems = computed(() => filterTree([
        {
            label: t('Home'),
            icon: 'pi pi-fw pi-home',
            to: '/'
        },
        {
            label: t('Dashboard'),
            icon: 'pi pi-fw pi-chart-line',
            to: '/project/pmo-dashboard',
            requiresPermission: ['pmo.dashboard', 'pmo.project-tracker', 'pmo.program-tracker', 'pmo.council-board', 'strategic.plans.read'],
            permissionFallbackRoles: [
                'PMO',
                ...PROJECT_ROLES,
                ...PROGRAM_ROLES,
                ...STRATEGY_ROLES,
            ],
        },
        {
            label: t('My Tasks'),
            icon: 'pi pi-fw pi-check-square',
            to: '/strategy/my-tasks'
        },
        {
            label: t('Istedama Library'),
            icon: 'pi pi-fw pi-book',
            to: '/library',
            requiresPermission: ['library.read'],
        },
        {
            label: t('PMO Library'),
            icon: 'pi pi-fw pi-folder-open',
            to: '/project/pmo-library',
        },
        {
            label: t('Org. Structure'),
            icon: 'pi pi-fw pi-sitemap',
            to: '/project/organizational-structure',
            blockedRoles: STRATEGY_ROLES,
        },

        {
            label: t('Initiatives'),
            icon: 'pi pi-fw pi-sitemap',
            blockedRoles: ['PMO', ...PROJECT_ROLES, ...PROGRAM_ROLES],
            items: [
                {
                    label: t('Initiative Views'),
                    icon: 'pi pi-fw pi-eye',
                    to: '/strategy/initiative-views',
                    requiresPermission: ['strategic.initiatives.read'],
                    permissionFallbackRoles: STRATEGY_ROLES,
                },
                {
                    label: t('Initiatives Roadmap'),
                    icon: 'pi pi-fw pi-calendar',
                    to: '/initiatives-roadmap',
                    requiresPermission: ['strategic.initiatives.read'],
                    permissionFallbackRoles: STRATEGY_ROLES,
                },
            ],
        },
        {
            label: t('Strategic House'),
            icon: 'pi pi-fw pi-building-columns',
            to: '/strategic-house',
            blockedRoles: ['PMO', ...PROJECT_ROLES, ...PROGRAM_ROLES],
        },
        {
            label: t('Strategy'),
            icon: 'pi pi-fw pi-compass',
            to: '/strategy/strategic-plans',
            requiresRole: STRATEGY_SIDEBAR_ROLES,
            // Initiatives, KPIs, risks, challenges and corrective plans have no
            // nav entry of their own: they are reached through this workspace,
            // so it stays highlighted while the user is inside them.
            activeFor: ['/strategy'],
            requiresPermission: ['strategic.plans.read', 'strategic.plans.create', 'strategic.plans.update', 'strategic.plans.approve'],
            permissionFallbackRoles: STRATEGY_SIDEBAR_ROLES,
        },
        {
            label: t('Annual Plan'),
            icon: 'pi pi-fw pi-calendar-clock',
            blockedRoles: [...PROJECT_ROLES, ...PROGRAM_ROLES],
            items: [
                {
                    // OPIs, department activities and the complete annual-plan register.
                    label: t('Annual Business Plan'),
                    icon: 'pi pi-fw pi-briefcase',
                    to: '/strategy/annual-business-plan/list',
                    requiresPermission: 'annual-business-plans.read',
                },
            ],
        },
        {
            label: t('Master Plan'),
            icon: 'pi pi-fw pi-objects-column',
            // Delivery of approved plans is project-side work: the strategy roles hand plans
            // over at approval and do not follow them here. A department works only its own
            // annual business plan, so the org-wide Master Plan register stays out of its way.
            blockedRoles: [...STRATEGY_ROLES, 'Strategy Relevant Department', 'Project Manager'],
            items: [
                {
                    label: t('Master Plan'),
                    icon: 'pi pi-fw pi-verified',
                    to: '/master-plan',
                    blockedRoles: 'Project Manager',
                    requiresPermission: 'annual-business-plans.read',
                    // PMO adds the Master Plan details and the Project Owner assigns approved
                    // plans, so both reach the page through their role as well.
                    permissionFallbackRoles: ['PMO', 'Project Owner'],
                },
            ],
        },
        {
            label: t('Projects'),
            icon: 'pi pi-fw pi-briefcase',
            to: '/project-management',
            blockedRoles: [...STRATEGY_ROLES, 'Strategy Relevant Department'],
            requiresPermission: 'annual-business-plans.read',
            // Approved plans are delivered like projects: their manager, the Project
            // Owner who assigns them and PMO all work from this standalone page.
            permissionFallbackRoles: [
                'PMO', 'Project Owner', 'Project Manager', 'Risk Section Director',
                'Project Sponsor', 'Sponsor',
            ],
        },
        {
            label: t('Tasks'),
            icon: 'pi pi-fw pi-check-square',
            to: '/project/tasks',
            hidden: true,
            blockedRoles: STRATEGY_ROLES,
            requiresPermission: 'projects.tasks.read',
        },
        // Programs and Portfolio are PMO governance surfaces. Both carry the permission
        // their route guard checks, so neither is ever shown and then refused on arrival.
        {
            label: t('Programs'),
            icon: 'pi pi-fw pi-sitemap',
            to: '/project/programs',
            requiresRole: PMO_ONLY_ROLES,
            requiresPermission: 'programs.read',
        },
        {
            label: t('Portfolio'),
            icon: 'pi pi-fw pi-briefcase',
            to: '/portfolio',
            requiresRole: PMO_ONLY_ROLES,
            requiresPermission: 'portfolios.read',
        },
        {
            label: t('Knowledge Hub'),
            icon: 'pi pi-fw pi-book',
            to: '/knowledge-hub',
            hidden: true,
        }

    ]));

    const bottomMenuItems = computed(() => filterTree([
        {
            label: t('Settings'),
            icon: 'pi pi-fw pi-cog',
            requiresRole: ['Super Admin'],
            items: [
                // Users/Roles/Permissions administration is shown in the sidebar again.
                // Program/Portfolio access itself stays hidden separately below (see
                // the Programs/Portfolio block in menuItems) — that restriction is
                // unrelated to whether this admin section is visible.
                {
                    label: t('Users'),
                    icon: 'pi pi-fw pi-user',
                    to: '/profile/list',
                    requiresPermission: 'users.read'
                },
                {
                    label: t('Roles'),
                    icon: 'pi pi-fw pi-shield',
                    to: '/roles',
                    requiresPermission: 'roles.read'
                },
                {
                    label: t('OPI Scoring'),
                    icon: 'pi pi-fw pi-sliders-h',
                    to: '/strategy/opi-settings'
                },
                {
                    label: t('Audit Log'),
                    icon: 'pi pi-fw pi-history',
                    to: '/strategy/audit-log',
                    requiresPermission: 'audit.read'
                },
                {
                    label: t('Permissions'),
                    icon: 'pi pi-fw pi-lock',
                    to: '/permissions',
                    requiresPermission: 'permissions.read'
                },
                {
                    label: t('Divisions'),
                    icon: 'pi pi-fw pi-sitemap',
                    to: '/divisions',
                    requiresPermission: 'divisions.read'
                },
                {
                    label: t('Departments'),
                    icon: 'pi pi-fw pi-building',
                    to: '/departments',
                    requiresPermission: 'departments.read'
                },
                // {
                //     label: t('Data Import'),
                //     icon: 'pi pi-fw pi-upload',
                //     to: '/data-import',
                //     requiresRole: ['Super Admin'],
                // },
            ]
        },
        {
            label: t('Logout'),
            icon: 'pi pi-fw pi-sign-out',
            action: 'logout'
        }
    ]));

    return { menuItems, bottomMenuItems };
}

// Keep backward compatibility with default export
const menu = [
    {
        label: 'Home',
        icon: 'pi pi-fw pi-home',
        to: '/'
    },
    {
        label: 'Dashboard',
        icon: 'pi pi-fw pi-chart-bar',
        to: '/dashboard'
    },
    {
        label: 'Istedama Library',
        icon: 'pi pi-fw pi-book',
        to: '/library'
    },
    {
        label: 'PMO Library',
        icon: 'pi pi-fw pi-folder-open',
        to: '/project/pmo-library'
    },
    {
        label: 'Organizational Structure',
        icon: 'pi pi-fw pi-sitemap',
        to: '/project/organizational-structure'
    },
    // Strategic House depends on Strategic Plans and is disabled with them.
    // {
    //     label: 'Strategic House',
    //     icon: 'pi pi-fw pi-building-columns',
    //     to: '/strategic-house'
    // },
    // Strategic Plans navigation is intentionally disabled for now.
    // {
    //     label: 'Strategy',
    //     icon: 'pi pi-fw pi-compass',
    //     to: '/strategy/strategic-plans'
    // },
    {
        label: 'Projects',
        icon: 'pi pi-fw pi-list',
        to: '/project/projects'
    },
    {
        label: 'Programs',
        icon: 'pi pi-fw pi-sitemap',
        to: '/project/programs'
    },
    {
        label: 'Knowledge Hub',
        icon: 'pi pi-fw pi-book',
        to: '/knowledge-hub'
    }
];

export const bottomMenu = [
    {
        label: 'Settings',
        icon: 'pi pi-fw pi-cog',
        requiresRole: ['Super Admin'],
        items: [
            // Users/Roles/Permissions administration is shown in the sidebar again
            // (kept in sync with the active useMenuItems config above).
            {
                label: 'Users',
                icon: 'pi pi-fw pi-user',
                to: '/profile/list'
            },
            {
                label: 'Roles',
                icon: 'pi pi-fw pi-shield',
                to: '/roles'
            },
            {
                label: 'Audit Log',
                icon: 'pi pi-fw pi-history',
                to: '/strategy/audit-log'
            },
            {
                label: 'Permissions',
                icon: 'pi pi-fw pi-lock',
                to: '/permissions'
            },
            {
                label: 'Divisions',
                icon: 'pi pi-fw pi-sitemap',
                to: '/divisions'
            },
            {
                label: 'Departments',
                icon: 'pi pi-fw pi-building',
                to: '/departments'
            },
        ]
    },
    {
        label: 'Logout',
        icon: 'pi pi-fw pi-sign-out',
        action: 'logout'
    }
];

export default menu;
